"use client";

import React from "react";
import { Loader, ShieldCheck, Zap } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function SupportFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="102586" className="flex flex-col items-center justify-center space-y-4 py-12 text-center animate-in fade-in zoom-in duration-500">
        <div className="flex size-16 items-center justify-center rounded-full bg-primary/10 text-primary ring-1 ring-primary/30">
          <ShieldCheck className="size-8" />
        </div>
        <h3 className="text-2xl font-sans font-bold text-foreground">Transmission Received</h3>
        <p className="text-muted-foreground max-w-md">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 rounded-sm border-muted-cobalt/40 hover:bg-primary/10">
          Initiate New Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-wider text-primary">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Julian Vane" {...field} className="bg-muted/30 border-muted/50 focus:border-primary/50" />
              </FormControl>
              <FormDescription className="text-muted-foreground/60">Primary contact identity</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-wider text-primary">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="strategy@firm.com" {...field} className="bg-muted/30 border-muted/50 focus:border-primary/50" />
              </FormControl>
              <FormDescription className="text-muted-foreground/60">Secure communication channel</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-wider text-primary">Direct Phone</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="206-929-1309" {...field} className="bg-muted/30 border-muted/50 focus:border-primary/50" />
              </FormControl>
              <FormDescription className="text-muted-foreground/60">Optional – for urgent technical advisory</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="website_url" rules={{
        pattern: {
          value: /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/,
          message: "Please enter a valid URL"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-wider text-primary">Corporate URL</FormLabel>
              <FormControl>
                <Input type="url" placeholder="https://enterprise.com" {...field} className="bg-muted/30 border-muted/50 focus:border-primary/50" />
              </FormControl>
              <FormDescription className="text-muted-foreground/60">The infrastructure requiring audit</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="subject" rules={{
      required: "Subject is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-wider text-primary">Optimization Objective</FormLabel>
            <FormControl>
              <Input placeholder="Workflow optimization inquiry" {...field} className="bg-muted/30 border-muted/50 focus:border-primary/50" />
            </FormControl>
            <FormDescription className="text-muted-foreground/60">e.g., "Digital workflow audit" or "Infrastructure scaling"</FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Please provide more details"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-wider text-primary">Strategic Context</FormLabel>
            <FormControl>
              <Textarea placeholder="Outline your current infrastructure challenges and strategic goals for specialized professional review." className="min-h-[120px] resize-y bg-muted/30 border-muted/50 focus:border-primary/50" {...field} />
            </FormControl>
            <FormDescription className="text-muted-foreground/60">Include current bottlenecks, desired outcomes, and technical specifications</FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-sm border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto min-w-[240px] h-12 text-base font-bold rounded-sm bg-gradient-to-r from-primary to-cyan-600 text-primary-foreground shadow-[0_0_20px_rgba(0,255,255,0.3)] hover:shadow-[0_0_30px_rgba(0,255,255,0.5)] transition-all duration-300">
        {isSubmitting ? <>
            <Loader className="mr-2 size-5 animate-spin" />
            Initializing Transmission...
          </> : <>
            <Zap className="mr-2 size-5" />
            Initialize Consultation
          </>}
      </Button>
    </Form>;
}
export default function SupportInquiry() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: formRef,
    isVisible: isFormVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="inquiry-portal" className="dark bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      {}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <div className="absolute h-[800px] w-[800px] rounded-full bg-primary/5 opacity-50 blur-[100px]" />
        <div className="absolute top-1/2 left-1/2 h-[400px] w-[400px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/10 opacity-40 blur-[80px]" />
      </div>

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div className="mx-auto max-w-3xl">
          <div ref={headerRef} className={`mb-12 text-center transition-all duration-700 ${isHeaderVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
            <h2 className="font-sans text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl uppercase">
              The First Step Toward Efficiency
            </h2>
            <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
              Century Pointe Holdings provides technical oversight and strategic advisory to streamline your digital infrastructure. Initialize your optimization journey below.
            </p>
          </div>

          <div ref={formRef} className={`transition-all duration-700 delay-200 ${isFormVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`}>
            <FormProvider formKey="inquiry_portal_optimization_strategy" formId="inquiry_portal_optimization_strategy" sectionName="Inquiry Portal" className="rounded-[20px] border border-border/50 bg-card/60 p-6 shadow-2xl backdrop-blur-xl sm:p-10" defaultValues={{
            name: "",
            email: "",
            phone: "",
            website_url: "",
            subject: "",
            message: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            subject: {
              required: true
            },
            message: {
              required: true
            }
          }} submitText="Initialize Consultation" successMessage="Your strategic inquiry has been logged. Our technical oversight team will review your infrastructure goals and contact you shortly.">
              <SupportFormInner />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}